import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Tooltip, Typography, makeStyles } from "@material-ui/core";
import { Close, LabelOffOutlined, PeopleOutline, Search } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { contactPresentation } from "../../pages/CrmRelationship/helpers";

const useStyles = makeStyles(theme => ({
  paper: { margin: 16, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" },
  title: { padding: "18px 20px 12px", "& h2": { display: "flex", alignItems: "center", gap: 10, fontSize: 20, fontWeight: 700 } },
  close: { marginLeft: "auto", color: theme.crm.text },
  tag: { fontSize: 13, color: theme.crm.muted, margin: "-4px 20px 16px", overflowWrap: "anywhere" },
  content: { padding: "0 20px", minHeight: 120 },
  search: { marginBottom: 14 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${theme.crm.border}`, "&:last-child": { borderBottom: 0 } },
  avatar: { width: 36, height: 36, fontSize: 13, fontWeight: 700, background: theme.crm.raised, color: theme.crm.accent },
  identity: { flex: 1, minWidth: 0 },
  name: { fontSize: 14, fontWeight: 650, overflowWrap: "anywhere" },
  secondary: { fontSize: 12, color: theme.crm.muted, overflowWrap: "anywhere", marginTop: 3 },
  remove: { color: theme.crm.text, borderRadius: 8, "&:hover": { color: theme.palette.error.main } },
  state: { textAlign: "center", padding: "32px 8px", color: theme.crm.muted, "& p": { marginBottom: 12 } },
  actions: { borderTop: `1px solid ${theme.crm.border}`, padding: "12px 20px", justifyContent: "space-between", color: theme.crm.muted, fontSize: 12 }
}));

export default function ContactTagListModal({ open, onClose, tag, onChanged }) {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [contacts, setContacts] = useState(tag.contacts || []);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [removing, setRemoving] = useState(null);
  const removingRef = useRef(null);
  const [visible, setVisible] = useState(25);
  const close = () => { if (!removingRef.current) onClose(); };

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    api.get(`/tags/${tag.id}`).then(({ data }) => {
      if (active) setContacts(data.contacts || []);
    }).catch(err => { if (active) { setError(true); toastError(err); } }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tag.id, open, revision]);

  useEffect(() => {
    if (!socket || !open) return undefined;
    const changed = data => {
      if (data.action === "update" && Number(data.tag?.id) === Number(tag.id)) setRevision(current => current + 1);
      if (data.action === "delete" && Number(data.tagId) === Number(tag.id)) onClose();
    };
    const channel = `company${user.companyId}-tag`;
    socket.on(channel, changed);
    return () => socket.off(channel, changed);
  }, [socket, user.companyId, tag.id, open, onClose]);

  const remove = async contact => {
    if (removingRef.current) return;
    removingRef.current = contact.id;
    setRemoving(contact.id);
    try {
      await api.delete(`/tags-contacts/${tag.id}/${contact.id}`);
      setContacts(current => current.filter(item => item.id !== contact.id));
      setRevision(current => current + 1);
      if (onChanged) onChanged();
      toast.success("Tag removida do contato.");
    } catch (err) { toastError(err); }
    finally { removingRef.current = null; setRemoving(null); }
  };
  const filtered = useMemo(() => {
    const normalize = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
    return contacts.map(contact => ({ contact, display: contactPresentation(contact, user.profile === "user") }))
      .filter(({ display }) => normalize(`${display.name} ${display.secondary}`).includes(normalize(search.trim())));
  }, [contacts, search, user.profile]);

  return <Dialog open={open} onClose={close} fullWidth maxWidth="sm" classes={{ paper: classes.paper }} aria-labelledby="tag-contacts-title">
    <DialogTitle id="tag-contacts-title" className={classes.title}><PeopleOutline />Contatos da tag<IconButton className={classes.close} disabled={Boolean(removing)} onClick={close} aria-label="Fechar contatos da tag"><Close /></IconButton></DialogTitle>
    <Typography className={classes.tag}>{tag.name}</Typography>
    <DialogContent className={classes.content}>
      <TextField className={classes.search} fullWidth size="small" variant="outlined" placeholder="Buscar contato" inputProps={{ "aria-label": "Buscar contato da tag" }} value={search} onChange={event => { setSearch(event.target.value); setVisible(25); }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      {loading ? <div className={classes.state}><CircularProgress size={26} aria-label="Carregando contatos da tag" /></div> : error ? <div className={classes.state} role="alert"><Typography>Não foi possível carregar os contatos.</Typography><Button onClick={() => setRevision(current => current + 1)}>Tentar novamente</Button></div> : <>
        {filtered.slice(0, visible).map(({ contact, display }) => <article key={contact.id} aria-label={display.name} className={classes.row}>
          <Avatar className={classes.avatar}>{display.initials}</Avatar>
          <div className={classes.identity}><Typography className={classes.name}>{display.name}</Typography><Typography className={classes.secondary}>{display.secondary}</Typography></div>
          <Tooltip title="Remover tag deste contato"><span><IconButton className={classes.remove} disabled={Boolean(removing)} aria-label={`Remover tag de ${display.name}`} onClick={() => remove(contact)}>{removing === contact.id ? <CircularProgress size={20} /> : <LabelOffOutlined fontSize="small" />}</IconButton></span></Tooltip>
        </article>)}
        {!filtered.length && <div className={classes.state}><Typography>{search ? "Nenhum contato encontrado" : "Nenhum contato vinculado"}</Typography></div>}
        {filtered.length > visible && <Button fullWidth onClick={() => setVisible(value => value + 25)}>Carregar mais contatos</Button>}
      </>}
    </DialogContent>
    <DialogActions className={classes.actions}><span>{filtered.length} {filtered.length === 1 ? "contato" : "contatos"}</span><Button variant="outlined" disabled={Boolean(removing)} onClick={close}>Fechar</Button></DialogActions>
  </Dialog>;
}
